import { ConnectingLineHandler } from "../ConnectingLineHandler";
import { lineCircleCollide } from "../../components/cell/grid-cell.utils";
import { DIAGRAM_EMIT_EVENTS } from "../../../../diagram/modules/DiagramEvents";
import { DiagramEventEmitter } from "../../../../diagram/modules/DiagramEventEmitter";

/**
 * @description Handles line selection
 */
export const LineSelection = (function () {
    const instance = {};
    class LineSelection {
        instanceUuid;

        lastSelectedLine = null;

        currentSelectedLine = null;

        lineHandler;

        lastEventEmittedSelection = null;

        LINE_CHECK_RADIUS = 3;

        eventEmitter;

        emitCustomProperties;

        constructor(diagramInstanceUuid) {
            if (!instance[diagramInstanceUuid]) {
                // setting the singleton instance
                instance[diagramInstanceUuid] = this;
                this.lineHandler = new ConnectingLineHandler(diagramInstanceUuid);
                this.instanceUuid = diagramInstanceUuid;
                this.eventEmitter = new DiagramEventEmitter(this.instanceUuid);
            }
            return instance[diagramInstanceUuid];
        }

        /**
         * @description Updates the line state
         * @param {String} uuid - the line uuid
         * @param {Boolean} isActive - the value to update directSelection with
         */
        updateLine(uuid, isActive) {
            if (uuid) {
                const connector = this.lineHandler.connectors.get(uuid);
                connector.instance.update({}, { directSelection: isActive });
                connector.instance.draw();
            }
        }

        /**
         * @description Handles the click selection on the line
         * @param {Object} payload  - {x,y} mouse position
         * @param {ConnectingLineHandler} labelHandler  -
         */
        handleClickSelection(payload, labelHandler) {
            this.updateLine(this.currentSelectedLine, false);
            this.lastSelectedLine = this.currentSelectedLine;
            this.emitCustomProperties = true;
            this.setSelectedLine(this.checkIfLineWasClicked(payload), false, labelHandler);
            this.emitCustomProperties = false;
            this.updateLine(this.currentSelectedLine, true);
            return this.currentSelectedLine;
        }

        /**
         * @description Checks if the line was clicked
         * @param {*} payload  - {x,y} mouse coordonates
         */
        checkIfLineWasClicked(payload) {
            const connectorKeys = this.lineHandler.connectors.keys();
            for (const uuid of connectorKeys) {
                const connector = this.lineHandler.connectors.get(uuid);
                const { path } = connector.instance.layout;
                for (let i = 0; i < path.length; i++) {
                    const pointA = path[i];
                    const pointB = path[i + 1];
                    if (pointA && pointB) {
                        const onLine = lineCircleCollide([pointA.x, pointA.y], [pointB.x, pointB.y], [payload.x, payload.y], this.LINE_CHECK_RADIUS);
                        if (onLine) {
                            return connector.instance.uuid;
                        }
                    }
                }
            }

            return null;
        }

        /**
         * @description checks if the poitn provided is on the line provided
         */
        isPointOnLine(x, y, endx, endy, px, py) {
            const f = function (somex) { return (endy - y) / (endx - x) * (somex - x) + y; };
            return Math.abs(f(px) - py) < 1e-6 // tolerance, rounding errors
                && px >= x && px <= endx; // are they also on this segment?
        }

        /**
         * @description sets the provided line as selected
         */
        setSelectedLine(lineUuid, noEventEmission, grid, labelHandler) {
            const line = this.lineHandler.connectors.get(lineUuid);
            if (line && line.instance.state.disabled) {
                return;
            }
            if (this.lastSelectedLine && this.lastSelectedLine !== lineUuid) {
                const connector = this.lineHandler.connectors.get(this.lastSelectedLine);
                if (connector) {
                    this.updateLine(this.lastSelectedLine, false);
                }
            }
            this.lastSelectedLine = this.currentSelectedLine;
            this.currentSelectedLine = lineUuid;

            if (this.currentSelectedLine) {
                const connector = this.lineHandler.connectors.get(this.currentSelectedLine);
                const fromCell = this.lineHandler.cells.get(connector.from).ref;
                this.selectStartEndPoints(connector, labelHandler);
                const fromCellContent = fromCell.getContentInstance();

                const toCell = this.lineHandler.cells.get(connector.to).ref;
                const toCellContent = toCell.getContentInstance();
                if (noEventEmission) {
                    this.updateLine(this.currentSelectedLine, true);
                    return;
                }
                if (this.currentSelectedLine === this.lastEventEmittedSelection || !toCellContent) {
                    return;
                }
                this.lastEventEmittedSelection = this.currentSelectedLine;
                this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CONNECTOR_SELECTED,
                    {
                        CONNECTOR_UUID: connector.instance.uuid, FROM_UUID: fromCellContent.uuid, FROM_ID: fromCellContent.data.id, TO_UUID: toCellContent.uuid, TO_ID: toCellContent.data.id, CUSTOM_EVENT_CONN_SELECTED: this.emitCustomProperties
                    });
            } else {
                if (this.lastSelectedLine) {
                    const connector = this.lineHandler.connectors.get(this.lastSelectedLine);
                    this.selectStartEndPoints(connector, labelHandler);
                }
                if (noEventEmission) {
                    this.updateLine(this.currentSelectedLine, true);
                    return;
                }
                if (this.currentSelectedLine === this.lastEventEmittedSelection) {
                    return;
                }
                this.lastEventEmittedSelection = null;
                this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CONNECTOR_SELECTED,
                    {
                        CONNECTOR_UUID: null, FROM_UUID: null, FROM_ID: null, TO_UUID: null, TO_ID: null
                    });
            }
            if (noEventEmission) {
                this.updateLine(this.currentSelectedLine, true);
            }
        }
        /**
         * @description trigger event for doubleClick event
         */

        handleDoubleClickEvent() {
            const connector = this.lineHandler.connectors.get(this.currentSelectedLine);
            const fromCell = this.lineHandler.cells.get(connector.from).ref;
            const fromCellContent = fromCell.getContentInstance();
            const toCell = this.lineHandler.cells.get(connector.to).ref;
            const toCellContent = toCell.getContentInstance();

            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CONNECTOR_DBLCLICK_SELECTED,
                {
                    CONNECTOR_UUID: connector.instance.uuid, FROM_UUID: fromCellContent.uuid, FROM_ID: fromCellContent.data.id, TO_UUID: toCellContent.uuid, TO_ID: toCellContent.data.id, doubleClickEvent: true
                });
        }

        /**
         * @description draws the cells and endpoints of the to and from cells
         */
        selectStartEndPoints(connector, labelHandler) {
            if (labelHandler) {
                this.lineHandler.connectors.forEach(element => {
                    const cellFrom = this.lineHandler.cells.get(element.from);
                    const cellTo = this.lineHandler.cells.get(element.to);
                    this.lineHandler.drawEndpoints(cellFrom);
                    this.lineHandler.drawEndpoints(cellTo);
                });
                this.lineHandler.redrawRequest(connector.from, labelHandler);
                this.lineHandler.redrawRequest(connector.to, labelHandler);
            }
        }

        /**
         * @description returns the selected value
         */
        getSelected() {
            return this.currentSelectedLine;
        }

        /**
         * @description destroys the current instance
         */
        destroy(instanceUuid) {
            this.lastSelectedLine = null;
            this.currentSelectedLine = null;
            this.lastEventEmittedSelection = null;
            instance[instanceUuid] = null;
            delete instance[instanceUuid];
        }

        /**
         * @description gets the data of the currently selected line
         */
        getSelectedLineData() {
            return this.lineHandler.connectors.get(this.currentSelectedLine);
        }
    }
    return LineSelection;
}());
